import { ErrorState } from "$components/error-state";
import { Table, type TableColumnDefinition } from "$components/table";
import { useUser } from "$global-providers/user-provider";
import {
  formatDatetime,
  formatUserName,
} from "$routes/inventory/_lib/formatting";
import UserName from "$routes/inventory/_lib/user-name";
import { gql } from "@apollo/client";
import { useState } from "react";
import { useIntl } from "react-intl";
import { useInventoryAuditTableQuery } from "./__generated__/inventory-audit-table.gql";
import { useAuditEventTypeMapping } from "./use-audit-event-type-mapping";

gql`
  query inventoryAuditTable($input: InventoryHistoryInput!, $options: InventoryHistoryOptions) {
    inventoryHistory(input: $input, options: $options) {
      totalCount
      items {
        timestamp
        eventType
        location {
          name
        }
        skuName
        user {
          person {
            firstname
            lastname
          }
        }
      }
    }
  }
`;

interface Props {
  pageSize?: number;
  locationId?: string;
  skuId?: string;
  startDate?: string;
  endDate?: string;
}

const InventoryAuditTable: FC<Props> = ({
  pageSize = 20,
  locationId,
  skuId,
  startDate,
  endDate,
}) => {
  const { formatMessage } = useIntl();

  const tableColumns: TableColumnDefinition[] = [
    {
      field: "date",
      headerName: formatMessage({
        id: "inventory.audit.table.columnLabel.date",
        defaultMessage: "Date",
      }),
      flex: 1,
    },
    {
      field: "user",
      headerName: formatMessage({
        id: "inventory.audit.table.columnLabel.user",
        defaultMessage: "Done By",
      }),
      flex: 2,
      renderCell: (val) => <UserName name={val} />,
    },
    {
      field: "eventType",
      headerName: formatMessage({
        id: "inventory.audit.table.columnLabel.eventType",
        defaultMessage: "Action",
      }),
      flex: 2,
    },
    {
      field: "location",
      headerName: formatMessage({
        id: "inventory.audit.table.columnLabel.location",
        defaultMessage: "Location",
      }),
      flex: 2,
    },
    {
      field: "sku",
      headerName: formatMessage({
        id: "inventory.audit.table.columnLabel.sku",
        defaultMessage: "Item Name",
      }),
      flex: 2,
    },
  ];

  const { facilityId } = useUser();
  const mapEventType = useAuditEventTypeMapping();

  const [currentPage, setCurrentPage] = useState(0);

  const { data, loading, error } = useInventoryAuditTableQuery({
    variables: {
      input: {
        facilityId,
        locationId,
        skuId,
      },
      options: {
        pageNumber: currentPage,
        pageSize,
        startDate,
        endDate,
      },
    },
    fetchPolicy: "cache-and-network",
  });

  if (error) {
    return <ErrorState variant="inline" error={error} />;
  }

  const mappedData = data?.inventoryHistory?.items?.map((item) => {
    const user = formatUserName(item.user);
    return {
      id: `${item.timestamp}-${item.eventType}-${user}`,
      date: formatDatetime(item.timestamp),
      eventType: mapEventType(item.eventType),
      user,
      sku: item.skuName,
      location: item.location?.name,
    };
  });

  return (
    <Table
      loading={loading}
      columns={tableColumns}
      rows={mappedData ?? []}
      noDataMessage={formatMessage({
        id: "inventory.audit.list.noData",
        defaultMessage: "No entries found.",
      })}
      pagination={{
        page: currentPage,
        pageSize,

        totalRows: data?.inventoryHistory?.totalCount,
        handlePageChange: (newPageNumber) => {
          setCurrentPage(newPageNumber);
        },
      }}
    />
  );
};

export default InventoryAuditTable;
